import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIntl } from "react-intl";
import { debounce } from "lodash";
import { createProductNumberAssignSchema } from "../utils/validationUtils";
import { MIN_NEW_PREFIX_MAGNITUDE } from "../utils/newPrefixMagnitude";
export var useProductNumberAssignForm = function (isAgencyWorker, publisherIdFromAssignment, previewResponse, setInternationalPrefixQuery, setPublisherQuery, internationalPrefixQuery, publisherQuery) {
    var formatMessage = useIntl().formatMessage;
    var _a = useForm({
        resolver: zodResolver(createProductNumberAssignSchema(formatMessage, isAgencyWorker)),
        defaultValues: {
            publisherId: "",
            agencyPrefixId: "",
            publisherPrefixId: "",
            magnitude: MIN_NEW_PREFIX_MAGNITUDE,
        },
        mode: "onBlur",
    }), handleSubmit = _a.handleSubmit, control = _a.control, watch = _a.watch, setValue = _a.setValue, errors = _a.formState.errors;
    var selectedPublisherId = watch("publisherId");
    var selectedInternationalPrefixId = watch("internationalPrefixId");
    var selectedAgencyPrefixId = watch("agencyPrefixId");
    var selectedPublisherPrefixId = watch("publisherPrefixId");
    var selectedMagnitude = watch("magnitude");
    useEffect(function () {
        if (selectedInternationalPrefixId) {
            setValue("agencyPrefixId", "");
            setValue("publisherPrefixId", "");
        }
    }, [selectedInternationalPrefixId, setValue]);
    useEffect(function () {
        if (selectedAgencyPrefixId) {
            setValue("publisherPrefixId", "");
        }
    }, [selectedAgencyPrefixId, setValue]);
    useEffect(function () {
        if (!isAgencyWorker && publisherIdFromAssignment) {
            setValue("publisherId", publisherIdFromAssignment);
        }
    }, [isAgencyWorker, publisherIdFromAssignment, setValue]);
    useEffect(function () {
        var _a, _b, _c;
        if (previewResponse) {
            if (((_a = previewResponse.internationalPrefix) === null || _a === void 0 ? void 0 : _a.id) &&
                !selectedInternationalPrefixId) {
                setValue("internationalPrefixId", previewResponse.internationalPrefix.id);
            }
            if (((_b = previewResponse.agencyPrefix) === null || _b === void 0 ? void 0 : _b.id) && !selectedAgencyPrefixId) {
                setValue("agencyPrefixId", previewResponse.agencyPrefix.id);
            }
            if (previewResponse.createNewPublisherPrefix &&
                ((_c = previewResponse.publisherPrefix) === null || _c === void 0 ? void 0 : _c.id) &&
                !selectedPublisherPrefixId) {
                setValue("publisherPrefixId", previewResponse.publisherPrefix.id);
            }
        }
    }, [
        previewResponse,
        selectedInternationalPrefixId,
        selectedAgencyPrefixId,
        selectedPublisherPrefixId,
        setValue,
    ]);
    var debouncedSetInternationalPrefixQuery = useMemo(function () { return debounce(function (value) { return setInternationalPrefixQuery(value); }, 300); }, [setInternationalPrefixQuery]);
    var debouncedSetPublisherQuery = useMemo(function () { return debounce(function (value) { return setPublisherQuery(value); }, 300); }, [setPublisherQuery]);
    var handleInternationalPrefixInput = function (inputValue) {
        if (inputValue === internationalPrefixQuery)
            return;
        debouncedSetInternationalPrefixQuery(inputValue);
    };
    var handleInternationalPrefixSelection = function (selectedInternationalPrefixId) {
        setValue("internationalPrefixId", selectedInternationalPrefixId, {
            shouldValidate: true,
        });
    };
    var handlePublisherInput = function (inputValue) {
        if (inputValue === publisherQuery)
            return;
        debouncedSetPublisherQuery(inputValue);
    };
    var handlePublisherSelection = function (selectedPublisherId, publishers) {
        var selectedPublisher = publishers.find(function (publisher) { return publisher.id === selectedPublisherId; });
        if (selectedPublisher) {
            setValue("publisherId", selectedPublisherId, { shouldValidate: true });
        }
    };
    var handleAgencyPrefixSelection = function (selectedAgencyPrefixId) {
        setValue("agencyPrefixId", selectedAgencyPrefixId, {
            shouldValidate: true,
        });
    };
    var handlePublisherPrefixSelection = function (selectedPublisherPrefixId) {
        setValue("publisherPrefixId", selectedPublisherPrefixId, {
            shouldValidate: true,
        });
    };
    var handleMagnitudeChange = function (magnitude) {
        setValue("magnitude", magnitude, { shouldValidate: true });
    };
    return {
        control: control,
        errors: errors,
        handleSubmit: handleSubmit,
        setValue: setValue,
        selectedPublisherId: selectedPublisherId,
        selectedInternationalPrefixId: selectedInternationalPrefixId,
        selectedAgencyPrefixId: selectedAgencyPrefixId,
        selectedPublisherPrefixId: selectedPublisherPrefixId,
        selectedMagnitude: selectedMagnitude,
        handleInternationalPrefixInput: handleInternationalPrefixInput,
        handleInternationalPrefixSelection: handleInternationalPrefixSelection,
        handlePublisherInput: handlePublisherInput,
        handlePublisherSelection: handlePublisherSelection,
        handleAgencyPrefixSelection: handleAgencyPrefixSelection,
        handlePublisherPrefixSelection: handlePublisherPrefixSelection,
        handleMagnitudeChange: handleMagnitudeChange,
    };
};
